import React from 'react';
import { ExternalLink, ArrowUp, ArrowDown, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { useInventory } from '../../context/InventoryContext';
import Badge from '../ui/Badge';

const RecentTransactions: React.FC = () => {
  const { transactions, products } = useInventory();
  
  // Sort transactions by date (recent first) and take the first 5
  const recentTransactions = [...transactions]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 8);
  
  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'in':
        return <ArrowDown className="h-4 w-4 text-green-600" />;
      case 'out':
        return <ArrowUp className="h-4 w-4 text-red-600" />;
      case 'adjustment':
        return <Pencil className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getTransactionBadge = (type: string) => {
    switch (type) {
      case 'in':
        return <Badge variant="success">In</Badge>;
      case 'out':
        return <Badge variant="danger">Out</Badge>;
      case 'adjustment':
        return <Badge variant="warning">Adj</Badge>;
      default:
        return null;
    }
  };

  const getProductDetails = (productId: string) => {
    const product = products.find(p => p.id === productId);
    return {
      name: product?.name || 'Product Not Found',
      sku: product?.sku || 'N/A'
    };
  };
  
  return (
    <Card className="overflow-hidden h-[28rem] transition-all duration-200 hover:shadow-md flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Recent Transactions</CardTitle>
        <Link 
          to="/transactions" 
          className="text-sm text-blue-900 hover:underline flex items-center"
        >
          View All <ExternalLink className="h-3 w-3 ml-1" />
        </Link>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        {recentTransactions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-gray-500 text-center">No transactions yet.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="p-4 space-y-3">
            {recentTransactions.map((transaction) => {
              const product = getProductDetails(transaction.product_id);
              
              return (
                <div key={transaction.id} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                  <div className="flex items-center">
                    <div className="p-1 rounded-full bg-gray-100">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(transaction.date)} • SKU: {product.sku}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {getTransactionBadge(transaction.type)}
                    <span className="ml-2 text-sm font-medium whitespace-nowrap">
                      {transaction.quantity}
                    </span>
                  </div>
                </div>
              );
            })}
            </div>
            {/* Scroll indicator */}
            {recentTransactions.length > 6 && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none flex items-end justify-center pb-1">
                <div className="text-xs text-gray-600">Scroll for more</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentTransactions;